import { SalesStatistic, UserStatistics } from "../../../models/Statistics";
import { Hidden } from "@material-ui/core";
import SalesByAgeGroup from "./SalesByAgeGroup";
import StatTable from "../components/StatTable";
import { Alert } from "@material-ui/lab";
import { Currency } from "../../../components/misc/Currency";
// @ts-ignore
import moment from "moment/moment";
import { useQuery } from "@tanstack/react-query";
import { get } from "../../../api/api";
import { ProblemDetails } from "../../../models/Http";
import { useI18n } from "../../../hooks/useI18n";

export default function AllSalesStatistics({
  dateRange
}: {
  dateRange: [Date, Date];
}) {
  const { t } = useI18n();
  const receiptStatistics = useReceiptStatisticsQuery(dateRange);
  const userStatistics = useUserStatisticsQuery(dateRange);

  return (
    <>
      <Hidden xsDown>
        <Alert severity={"info"}>
          {t("common.messages.actualSalesAfterCampaignDiscounts")}
        </Alert>
        <StatTable
          isLoading={receiptStatistics.isLoading}
          exportName={"Gross sale statistics"}
          columns={[
            {
              title: "Tenant",
              field: "tenantName"
            },
            {
              title: "Store",
              field: "storeName"
            },
            {
              title: "Sales / period",
              field: "periodsum",
              render: (rowData: any) => <Currency value={rowData.periodsum} />
            },
            {
              title: "Purchases / period",
              field: "periodcount"
            },
            {
              title: "Unique users / period",
              field: "uniqueUsers"
            },
            {
              title: "New users / period",
              field: "newUsers"
            },
            {
              title: "Sales / Day",
              field: "dailysum",
              cellStyle: { borderLeft: "1px solid #e0e0e0" },
              render: (rowData: any) => <Currency value={rowData.dailysum} />
            },
            {
              title: "Purchases / Day",
              field: "dailycount"
            }
          ]}
          data={
            !receiptStatistics.isLoading
              ? receiptStatistics.data?.map(r => ({
                  tenantName: r.tenantName,
                  storeName: r.storeName,
                  periodsum: r.period.sum,
                  periodcount: r.period.count,
                  uniqueUsers: r.uniqueUsers,
                  newUsers: r.newUsers,
                  dailysum: r.daily.sum,
                  dailycount: r.daily.count.toFixed(2)
                }))
              : []
          }
          detailPanel={(rowData: any) => (
            <SalesByAgeGroup
              storeName={rowData.storeName}
              userStatistics={userStatistics.data!}
              periodFrom={dateRange[0]}
              periodTo={dateRange[1]}
              isLoading={userStatistics.isLoading}
            />
          )}
        />
      </Hidden>
      <Hidden smUp>
        <StatTable
          isLoading={receiptStatistics.isLoading}
          columns={[
            {
              title: "Store",
              field: "storeName"
            },
            {
              title: "Sales / period",
              field: "periodsum",
              render: (rowData: any) => <Currency value={rowData.periodsum} />
            },
            {
              title: "Purchases / period",
              field: "periodcount"
            }
          ]}
          data={
            !receiptStatistics.isLoading
              ? receiptStatistics.data?.map(r => ({
                  storeName: r.storeName,
                  periodsum: r.period.sum,
                  periodcount: r.period.count
                }))
              : []
          }
        />
      </Hidden>
    </>
  );
}

function fetchAverageReceiptStatistics(dateRange: [Date, Date]) {
  return async () => {
    const [from, to] = dateRange;
    const response = await get(
      `/v1/statistics/all-sales?periodFrom=${moment(from).format(
        "YYYY-MM-DD"
      )}&periodTo=${moment(to).format("YYYY-MM-DD")}`
    );
    const content = await response.json();

    if (!response.ok) {
      throw new content();
    }
    return content;
  };
}

function fetchUserStatistics(dateRange: [Date, Date]) {
  return async () => {
    const [from, to] = dateRange;
    const response = await get(
      `/v1/statistics/all-user?periodFrom=${moment(from).format(
        "YYYY-MM-DD"
      )}&periodTo=${moment(to).format("YYYY-MM-DD")}`
    );
    const content = await response.json();

    if (!response.ok) {
      throw new content();
    }
    return content;
  };
}

const useReceiptStatisticsQuery = (dateRange: [Date, Date]) =>
  useQuery<ReadonlyArray<SalesStatistic>, ProblemDetails>({
    queryKey: ["all-receipt-statistics", dateRange],
    queryFn: fetchAverageReceiptStatistics(dateRange),
    staleTime: Infinity
  });

const useUserStatisticsQuery = (dateRange: [Date, Date]) =>
  useQuery<ReadonlyArray<UserStatistics>, ProblemDetails>({
    queryKey: ["all-user-statistics", dateRange],
    queryFn: fetchUserStatistics(dateRange),
    staleTime: Infinity
  });
